/**
 * Converts the live hook states from the dashboard into a flat,
 * Firestore-safe conditions snapshot. Stores raw numeric values
 * (not display labels) so data stays accurate if units change later.
 *
 * Saltwater passes tideStage/nextTide (unchanged). Freshwater passes
 * waterMovement (USGS flow/level trend), cloudCover, seasonPhase, and an
 * explicit waterTempF (from the gage). These extra fields are omitted for
 * saltwater so its snapshot — and its Prep Score — stay byte-identical.
 */
export function buildConditionsSnapshot({
  moon,
  solunarMajor,
  waterTemp,
  wind,
  barometric,
  tideStage,
  nextTide,
  waterMovement,
  cloudCover,
  seasonPhase,
  waterTempF,
}) {
  // Prefer an explicit freshwater gage temperature when provided, else fall
  // back to the useWaterTemp hook's Celsius reading.
  let waterTempSnap = null;
  if (waterTempF != null && !Number.isNaN(Number(waterTempF))) {
    const f = Number(waterTempF);
    waterTempSnap = { celsius: Math.round(((f - 32) * 5) / 9), fahrenheit: Math.round(f) };
  } else if (waterTemp?.tempC != null) {
    waterTempSnap = {
      celsius: waterTemp.tempC,
      fahrenheit: Math.round((waterTemp.tempC * 9) / 5 + 32),
    };
  }

  return {
    tideStage: tideStage || null,
    nextTide: nextTide?.label || null,

    // Freshwater water-movement (USGS). Categorical trend feeds the same
    // score axis that tide feeds for saltwater.
    waterMovement: waterMovement?.trend
      ? {
          trend: waterMovement.trend,
          primaryMetric: waterMovement.primaryMetric ?? null,
          cfs: waterMovement.flowCfs ?? null,
          gageHeightFt: waterMovement.gageHeightFt ?? null,
          reservoirElevFt: waterMovement.reservoirElevFt ?? null,
          turbidity: waterMovement.turbidity ?? null,
        }
      : null,

    cloudCover: cloudCover?.percent != null ? { percent: cloudCover.percent } : null,

    seasonPhase: seasonPhase?.label ? { label: seasonPhase.label } : null,

    moonPhase: moon?.error
      ? null
      : {
          name: moon?.name ?? null,
          fullness: moon?.fullness ?? null,
          phase: moon?.phase ?? null,
          waxing: moon?.waxing ?? null,
        },

    solunarMajor: solunarMajor?.status === "warn"
      ? null
      : {
          label: solunarMajor?.label ?? null,
          type: solunarMajor?.type ?? null,
        },

    waterTemp: waterTempSnap,

    wind: wind?.error
      ? null
      : {
          speedKnots: wind?.speedKnots ?? null,
          direction: wind?.direction ?? null,
        },

    barometric: barometric?.error
      ? null
      : {
          inHg: barometric?.pressureInHg ?? null,
          trend: barometric?.trend ?? null,
        },
  };
}
